import {
  Checkbox,
  FieldLabel,
  FlexBox,
  HelpIconProps,
} from '@dixa/lib-shared-habla';
import { PropsWithClassName } from 'lib-shared-types-utils';
import { ComponentPropsWithoutRef, ReactNode } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';

import { HookFormBaseProps, useHookForm } from 'utils/hookForm';

type HookFormCheckboxProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = HookFormBaseProps<
  TFieldValues,
  TName,
  Pick<ComponentPropsWithoutRef<typeof Checkbox>, 'disabled'>
> &
  PropsWithClassName & {
    /** The label. */
    label?: ReactNode;
    /** The help icon and tooltip. */
    helpIcon?: HelpIconProps;
  };

/** Represents the React Hook Form variant of the {@link Checkbox} component. */
export const HookFormCheckbox = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: HookFormCheckboxProps<TFieldValues, TName>,
): JSX.Element => {
  const {
    controller: { field },
    controllerProps: { name: id },
  } = useHookForm(props);

  return (
    <FlexBox gap="200" alignItems="center" className={props.className}>
      <Checkbox
        {...field}
        id={id}
        checked={field.value}
        disabled={props.componentProps?.disabled}
      />

      {props.label && (
        <FieldLabel htmlFor={id} helpIcon={props.helpIcon}>
          {props.label}
        </FieldLabel>
      )}
    </FlexBox>
  );
};
